import React, {useEffect, useState} from 'react';
import classNames from "classnames/bind";
import css from './RadioGroup.module.scss'

// @utils
import {getCssClasses} from './utils';

// @constants
import {RadioButtonAccents, Sizes} from './constants';

export type TRadioGroup = {
    name: string;
    size?: string;
    accent?: string;
    error?: string;
    title?: string;
    disabled?: boolean;
    wrapperClassName?: string;
    titleClassName?: string;
    listItemClassName?: string;
    labelClassName?: string;
    captionClassName?: string;
    errorClassName?: string;
    value: string | undefined;
    testAttr?: string;
    options: { value: string; label: string | JSX.Element }[];
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    className?: string;
};

const cx = classNames.bind(css)

const RadioGroup: React.FC<TRadioGroup> = ({
                                               name,
                                               size = Sizes.M,
                                               accent = RadioButtonAccents.Blue,
                                               error,
                                               title,
                                               disabled = false,
                                               wrapperClassName,
                                               titleClassName,
                                               listItemClassName,
                                               labelClassName,
                                               captionClassName,
                                               errorClassName,
                                               value = '',
                                               options,
                                               onChange,
                                               testAttr = 'radio-group',
                                               className = '',
                                               ...props
                                           }) => {
    const {
        _wrapperClassName,
        _titleClassName,
        _listItemClassName,
        _labelClassName,
        _radioButtonClassName,
        _captionClassName,
        _errorClassName,
    } = getCssClasses({
        size,
        accent,
        disabled,
        wrapperClassName,
        titleClassName,
        listItemClassName,
        labelClassName,
        captionClassName,
        errorClassName,
    });

    const [currentValue, setCurrentValue] = useState(value);

    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    return (
        <div className={cx(_wrapperClassName, className)}>
            <ul>
                {options.map(({value: optionValue, label}) => {
                    const itemId = `${name}_${optionValue}`;

                    return (
                        <li className={_listItemClassName} key={optionValue}>
                            <label className={_labelClassName} htmlFor={itemId}>
                                <input
                                    type="radio"
                                    value={optionValue}
                                    name={name}
                                    checked={String(optionValue) === String(currentValue)}
                                    onChange={e => {
                                        setCurrentValue(e.target.value);
                                        onChange && onChange(e);
                                    }}
                                    id={itemId}
                                    disabled={disabled}
                                    data-test-attr={`${testAttr}-${optionValue}`}
                                    {...props}
                                />
                                <div className={_radioButtonClassName}/>
                                <div className={_captionClassName}>{label}</div>
                            </label>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
